// M18: light (default) / dark theme, remembered per browser, applied as <html data-theme>. Canvas drawings
// read their colours from the CSS variables in global.css through cssVar(), so no colour is hard-coded
// outside global.css.
import { create } from 'zustand';

export type Theme = 'light' | 'dark';
const KEY = 'epb.theme';

function initial(): Theme {
  try {
    return globalThis.localStorage?.getItem(KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function apply(t: Theme) {
  if (typeof document !== 'undefined') document.documentElement.dataset.theme = t;
  cache.clear();
}

export const useTheme = create<{ theme: Theme; setTheme: (t: Theme) => void }>((set) => ({
  theme: initial(),
  setTheme: (theme) => {
    try {
      globalThis.localStorage?.setItem(KEY, theme);
    } catch {
      /* not remembered */
    }
    apply(theme);
    set({ theme });
  },
}));

const cache = new Map<string, string>();
/** The current value of a CSS variable from global.css (e.g. cssVar('--photo-surround')). */
export function cssVar(name: string): string {
  const hit = cache.get(name);
  if (hit) return hit;
  if (typeof document === 'undefined') return '#808080';
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#808080';
  cache.set(name, v);
  return v;
}

/** A CSS colour variable as 0..1 RGB (hex or rgb() forms), for WebGL clears. */
export function cssRgb(name: string): [number, number, number] {
  const v = cssVar(name);
  const hex = /^#([0-9a-f]{6})$/i.exec(v);
  if (hex)
    return [0, 2, 4].map((i) => parseInt(hex[1]!.slice(i, i + 2), 16) / 255) as [number, number, number];
  const m = /rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(v);
  if (m) return [Number(m[1]) / 255, Number(m[2]) / 255, Number(m[3]) / 255];
  return [0.502, 0.502, 0.502];
}

if (typeof document !== 'undefined') apply(initial());
